import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import CreateProgress from "@/components/CreateProgress";
import RegenChoiceDialog from "@/components/RegenChoiceDialog";
import { ReferralCard } from "@/components/ReferralCard";
import { CommercialAuthDialog } from "@/components/CommercialAuthDialog";
import { StemsSection } from "@/components/StemsSection";
import { AudioApprovalCard } from "@/components/song/AudioApprovalCard";
import { AudioRegenConfirmModal } from "@/components/song/AudioRegenConfirmModal";
import { EmailDeliveryCard } from "@/components/song/EmailDeliveryCard";
import { Play, Pause, Download, ArrowLeft, Lock, ArrowsClockwise, Upload, MusicNotes, Warning, WhatsappLogo, Trophy, CurrencyCircleDollar, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SongDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const [song, setSong] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [regenInfo, setRegenInfo] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const audioRef = useRef(null);
  const coverInputRef = useRef(null);
  const [commercialOpen, setCommercialOpen] = useState(false);

  // Audio approval (P0): once the user explicitly authorizes the audio, we hide
  // the "Sí me gustó / Quiero modificar" CTA card. State is persisted in
  // localStorage by song id so the user doesn't see it again on reload.
  const [audioApproved, setAudioApproved] = useState(() => {
    try { return localStorage.getItem(`tm_audio_approved_${id}`) === "1"; }
    catch { return false; }
  });
  const [showAudioRegenConfirm, setShowAudioRegenConfirm] = useState(false);

  const load = () => api.get(`/songs/${id}`).then(r => setSong(r.data));
  const loadRegen = () => api.get(`/songs/${id}/regen-info`).then(r => setRegenInfo(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadRegen(); }, [id]);

  // Poll Suno generation status while the song is being composed (1-2 min)
  useEffect(() => {
    if (song?.status !== "generating_audio") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/songs/${id}/audio-status`);
        if (data.status === "audio_ready") {
          clearInterval(t);
          toast.success("¡Tu canción está lista! 🎵");
          await load();
          await loadRegen();
        } else if (data.status === "regen_failed") {
          clearInterval(t);
          toast.error("La regeneración falló. Se restauró tu audio anterior y se devolvió tu cambio.");
          await load();
          await refreshUser();
          await loadRegen();
        } else if (data.status === "audio_failed") {
          clearInterval(t);
          toast.error("La generación del audio falló. Puedes reintentar gratis.");
          await load();
        }
      } catch (err) {
        // Transient network errors during polling are expected; keep polling
        if (process.env.NODE_ENV === "development") {
          console.debug("[SongDetail] audio-status poll failed:", err?.message);
        }
      }
    }, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song?.status, id]);

  const retryAudio = async () => {
    setRegenLoading(true);
    try {
      const { data } = await api.post(`/songs/${id}/retry-audio`);
      if (data.status === "generating_audio") {
        toast.success("Reintentando la generación…");
        setSong(s => ({ ...s, status: "generating_audio", audio_url: null }));
      } else {
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error reintentando");
    } finally { setRegenLoading(false); }
  };

  const [contestOpen, setContestOpen] = useState(false);
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [entering, setEntering] = useState(false);
  const [entered, setEntered] = useState(false);

  const enterContest = async () => {
    setEntering(true);
    try {
      await api.post("/contest/enter", { song_id: id, tiktok_url: tiktokUrl.trim() || null });
      setEntered(true);
      setContestOpen(false);
      toast.success("¡Ya estás participando en el concurso! 🏆");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar tu participación");
    } finally { setEntering(false); }
  };

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
  };

  const onTime = () => {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
    setDuration(audioRef.current.duration || 0);
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    audioRef.current.currentTime = (x / rect.width) * duration;
  };

  const fmt = (s) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60); const r = Math.floor(s % 60);
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  const download = async () => {
    try {
      const token = localStorage.getItem("tm_token");
      const res = await fetch(`${API}/songs/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error descargando");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${song.title} - ${song.artist_name}.mp3`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("¡Canción descargada!");
    } catch (e) {
      toast.error(e.message);
    }
  };

  // If the audio_url changes (e.g. via regenerate-audio), clear the approval
  useEffect(() => {
    if (!song?.audio_url) return;
    try {
      const lastUrl = localStorage.getItem(`tm_audio_approved_url_${id}`);
      if (lastUrl && lastUrl !== song.audio_url) {
        localStorage.removeItem(`tm_audio_approved_${id}`);
        setAudioApproved(false);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song?.audio_url, id]);

  const approveAudio = () => {
    setAudioApproved(true);
    try {
      localStorage.setItem(`tm_audio_approved_${id}`, "1");
      if (song?.audio_url) localStorage.setItem(`tm_audio_approved_url_${id}`, song.audio_url);
    } catch { /* ignore */ }
    toast.success("¡Listo! Tu canción quedó autorizada 🎵");
  };

  const openAudioRegenConfirm = () => {
    setShowAudioRegenConfirm(true);
  };

  const confirmAudioRegen = async () => {
    setShowAudioRegenConfirm(false);
    // Directly trigger audio regen (lo que el usuario espera al modificar la música)
    setRegenLoading(true);
    try {
      const { data } = await api.post(`/songs/${id}/regenerate-audio`);
      await loadRegen();
      await refreshUser();
      if (data.generating) {
        setSong(s => ({ ...s, status: "generating_audio", audio_url: null }));
        toast.success(data.free
          ? "Generando nuevo audio (cambio gratis)… toma 1-2 minutos"
          : "Generando nuevo audio (-1 cambio extra)… toma 1-2 minutos");
        return;
      }
      setSong(s => ({ ...s, audio_url: data.audio_url }));
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.load(); }
      toast.success(data.free ? "Audio regenerado (cambio gratis)" : "Audio regenerado (-1 cambio extra)");
    } catch (err) {
      if (err?.response?.status === 402) { toast.error("Sin cambios extra"); nav("/cambios-extra"); return; }
      toast.error(err?.response?.data?.detail || "Error regenerando audio");
    } finally { setRegenLoading(false); }
  };

  const openRegenDialog = () => {
    const audioInfo = regenInfo?.kinds?.audio || {};
    const lyricsInfo = regenInfo?.kinds?.lyrics || {};
    const credits = regenInfo?.regen_credits || 0;
    // If user has no free changes left and no credits, send them straight to buy
    const anyFree = audioInfo.is_free_next || lyricsInfo.is_free_next;
    if (!anyFree && credits <= 0) { nav("/cambios-extra"); return; }
    setRegenDialogOpen(true);
  };

  const handleRegenChoice = async (choice) => {
    setRegenDialogOpen(false);
    if (choice === "lyrics") {
      // Send the user back to CreateSong step 2 (lyrics editor) where the
      // regenerate-lyrics flow already exists.
      nav(`/create?song_id=${id}`);
      return;
    }
    if (choice === "both") {
      // Both: navigate to CreateSong for lyrics regen first. After saving the
      // new lyrics there, the audio is naturally regenerated on the "Generar"
      // step. We pass a flag so the lyrics editor knows to nudge the user.
      toast.info("Te llevamos a editar la letra. El audio se regenera después al confirmar.");
      nav(`/create?song_id=${id}&regen=both`);
      return;
    }
    // choice === "audio" — direct regen via the existing endpoint
    setRegenLoading(true);
    try {
      const { data } = await api.post(`/songs/${id}/regenerate-audio`);
      await loadRegen();
      await refreshUser();
      if (data.generating) {
        setSong(s => ({ ...s, status: "generating_audio", audio_url: null }));
        toast.success(data.free
          ? "Generando nuevo audio (cambio gratis)… toma 1-2 minutos"
          : "Generando nuevo audio (-1 cambio extra)… toma 1-2 minutos");
        return;
      }
      setSong(s => ({ ...s, audio_url: data.audio_url }));
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.load(); }
      toast.success(data.free ? "Audio regenerado (cambio gratis)" : "Audio regenerado (-1 cambio extra)");
    } catch (err) {
      if (err?.response?.status === 402) { toast.error("Sin cambios extra"); nav("/cambios-extra"); return; }
      toast.error(err?.response?.data?.detail || "Error regenerando audio");
    } finally { setRegenLoading(false); }
  };

  const regenerateAudio = async () => {
    // Legacy entrypoint preserved for any callers — now opens the choice dialog.
    openRegenDialog();
  };

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const credits = regenInfo?.regen_credits || 0;
    if (credits <= 0) { toast.error("Necesitas 1 cambio extra para cambiar la portada"); nav("/cambios-extra"); return; }
    if (!window.confirm(`Cambiar la portada cuesta 1 cambio extra ($20 MXN). ¿Continuar?`)) {
      e.target.value = "";
      return;
    }
    setRegenLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/songs/${id}/regenerate-cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSong(s => ({ ...s, cover_image_b64: data.cover_data_url.replace("data:image/jpeg;base64,", "") }));
      await loadRegen();
      await refreshUser();
      toast.success("Portada regenerada");
    } catch (err) {
      if (err?.response?.status === 402) { toast.error("Sin cambios extra"); nav("/cambios-extra"); return; }
      toast.error(err?.response?.data?.detail || "Error regenerando portada");
    } finally {
      setRegenLoading(false);
      e.target.value = "";
    }
  };

  if (!song) return (
    <div className="min-h-screen bg-[#050505]"><Navbar /><p className="text-center py-20 text-white/40">Cargando…</p></div>
  );

  const paid = song.payment_status === "paid" && song.audio_url;
  const generatingAudio = song.status === "generating_audio";
  const audioFailed = song.status === "audio_failed";

  return (
    <div className="min-h-screen bg-[#050505] pb-32">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/library" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6" data-testid="song-back">
          <ArrowLeft size={16} /> Mis canciones
        </Link>

        {/* Visual flow progress — step 4 if generating, 5 if ready */}
        <CreateProgress
          currentStep={song.status === "audio_ready" ? 5 : 4}
          reachableMax={song.status === "audio_ready" ? 5 : 4}
          onStepClick={() => {}}
        />

        {/* Cover */}
        <div className="rounded-3xl overflow-hidden bg-[#121212] shadow-2xl mb-6 max-w-sm mx-auto">
          {song.cover_image_b64 ? (
            <img src={`data:image/jpeg;base64,${song.cover_image_b64}`} alt={song.title} className="w-full aspect-square object-cover" data-testid="song-cover" />
          ) : (
            <div data-testid="song-cover" className="w-full aspect-square bg-gradient-to-br from-[#FFB800]/20 to-[#FF4B00]/20" />
          )}
        </div>

        <div className="text-center mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="song-title">{song.title}</h1>
          <p className="text-white/60 mt-2" data-testid="song-artist">{song.artist_name}</p>
          <p className="text-xs uppercase tracking-widest text-[#FFB800] mt-2 capitalize">{song.genre.replace(/_/g, ' ')}</p>
        </div>

        {paid ? (
          <>
            <audio
              ref={audioRef}
              src={song.audio_url}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onTimeUpdate={onTime}
              onLoadedMetadata={onTime}
              preload="metadata"
            />

            {/* Seek bar */}
            <div onClick={seek} className="h-1.5 rounded-full bg-white/10 cursor-pointer mb-2 group" data-testid="song-seekbar">
              <div className="h-full rounded-full bg-[#FFB800] transition-all" style={{ width: `${(progress / (duration || 1)) * 100}%` }}></div>
            </div>
            <div className="flex justify-between text-xs text-white/40 mb-6">
              <span>{fmt(progress)}</span>
              <span>{fmt(duration)}</span>
            </div>

            <div className="flex justify-center items-center gap-6 mb-6">
              <button
                onClick={toggle}
                className="w-16 h-16 rounded-full bg-[#FFB800] hover:bg-[#E6A600] flex items-center justify-center active:scale-95 transition-all"
                data-testid="song-play-btn"
              >
                {playing ? <Pause weight="fill" size={26} className="text-black" /> : <Play weight="fill" size={26} className="text-black ml-1" />}
              </button>
            </div>

            {/* P0: Audio approval CTA — only visible until the user approves */}
            <AudioApprovalCard
              approved={audioApproved}
              regenInfo={regenInfo}
              loading={regenLoading}
              onApprove={approveAudio}
              onModify={openAudioRegenConfirm}
            />

            <button
              onClick={download}
              className="w-full py-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 font-semibold flex items-center justify-center gap-2 transition-all"
              data-testid="song-download-btn"
            >
              <Download weight="bold" size={18} /> Descargar HD con portada
            </button>

            <button
              onClick={() => {
                const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/song/${song.id}`;
                const text = encodeURIComponent(`🎵 Escucha "${song.title}", mi canción creada con mi historia en Tu MusiKa:\n${shareUrl}`);
                window.open(`https://wa.me/?text=${text}`, "_blank");
              }}
              className="w-full mt-3 py-4 rounded-full bg-[#25D366] hover:bg-[#1FB858] text-black font-semibold flex items-center justify-center gap-2 transition-all active:scale-95"
              data-testid="song-share-whatsapp-btn"
            >
              <WhatsappLogo weight="fill" size={20} /> Compartir por WhatsApp
            </button>
            <p className="text-center text-xs text-white/40 mt-3" data-testid="download-hint">
              💾 Al descargar, el MP3 se guarda en tu teléfono con la portada integrada.
              Búscalo en tu carpeta de <span className="text-white/70">Descargas</span> y compártelo donde quieras.
            </p>

            {/* Email delivery — non-blocking, optional. Doesn't affect WhatsApp viral flow. */}
            <div className="mt-4">
              <EmailDeliveryCard
                songId={id}
                defaultEmail={user?.email || ""}
                initialSent={!!(song.emailed_to || []).includes((user?.email || "").toLowerCase())}
              />
            </div>

            {/* Commercial use authorization — earn royalties */}
            {song.commercial_use ? (
              <div className="mt-4 rounded-2xl bg-[#B4F34A]/10 border border-[#B4F34A]/30 p-4 flex items-start gap-3" data-testid="commercial-authorized">
                <CheckCircle weight="fill" size={22} className="text-[#B4F34A] shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-bold text-[#B4F34A]">Autorizada para uso comercial</p>
                  <p className="text-xs text-white/60 mt-1 leading-relaxed">
                    Tu canción ya participa para entrar al TOP mensual. Si la seleccionamos, comenzará a generar regalías.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl bg-gradient-to-br from-[#0e1d10] to-[#0a0a0a] border border-[#B4F34A]/30 p-5" data-testid="commercial-cta-card">
                <div className="flex items-start gap-3 mb-3">
                  <CurrencyCircleDollar weight="fill" size={24} className="text-[#B4F34A] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-bold text-lg">¿Quieres ganar regalías con esta canción?</p>
                    <p className="text-xs text-white/60 mt-1 leading-relaxed">
                      Autorízanos a integrarla a la selección del TOP mensual. Si entra, formará parte del repertorio que vendemos a restaurantes y bares.
                      <span className="text-[#B4F34A] font-semibold"> El 60% de lo recaudado se reparte entre los creadores.</span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setCommercialOpen(true)}
                  className="w-full py-3 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black font-bold active:scale-95 transition-all"
                  data-testid="commercial-auth-btn"
                >
                  Autorizar uso comercial
                </button>
              </div>
            )}

            {/* Stem separation — Demucs via Replicate */}
            <StemsSection
              songId={id}
              songTitle={song.title}
              audioReady={song.status === "audio_ready" && !!song.audio_url}
            />

            {/* Contest entry */}
            {!entered && !contestOpen && (
              <button
                onClick={() => setContestOpen(true)}
                className="w-full mt-3 py-4 rounded-full bg-transparent border-2 border-[#FFB800]/50 hover:border-[#FFB800] text-[#FFB800] font-semibold flex items-center justify-center gap-2 transition-all"
                data-testid="song-contest-btn"
              >
                <Trophy weight="fill" size={18} /> Participar en el concurso del mes
              </button>
            )}
            {entered && (
              <p className="text-center text-sm text-[#FFB800] mt-3 font-semibold" data-testid="song-contest-entered">
                🏆 ¡Estás participando! <Link to="/concurso" className="underline">Ver el concurso</Link>
              </p>
            )}
            {contestOpen && (
              <div className="mt-3 rounded-2xl border border-[#FFB800]/40 bg-[#FFB800]/5 p-4" data-testid="song-contest-form">
                <p className="font-display font-bold text-sm mb-1">🏆 Concurso de Julio</p>
                <p className="text-xs text-white/60 mb-3">
                  La comunidad vota y el ganador llega a Spotify con promoción pagada.
                  <span className="text-white/80"> Tip:</span> sube un TikTok con tu canción y el hashtag
                  <span className="text-[#FFB800]"> #TuMusiKa</span> — pega aquí el enlace (opcional).
                </p>
                <input
                  value={tiktokUrl}
                  onChange={(e) => setTiktokUrl(e.target.value)}
                  placeholder="https://www.tiktok.com/... (opcional)"
                  className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 focus:border-[#FFB800] focus:outline-none text-sm placeholder-white/30 mb-3"
                  data-testid="song-contest-tiktok"
                />
                <div className="flex gap-2">
                  <button
                    onClick={enterContest}
                    disabled={entering}
                    className="flex-1 py-3 rounded-full bg-[#FFB800] hover:bg-[#E6A600] disabled:opacity-50 text-black font-semibold text-sm"
                    data-testid="song-contest-confirm"
                  >
                    {entering ? "Registrando…" : "¡Participar!"}
                  </button>
                  <button onClick={() => setContestOpen(false)} className="px-4 py-3 rounded-full bg-white/5 text-sm text-white/60">
                    Ahora no
                  </button>
                </div>
              </div>
            )}

            {/* REGEN ACTIONS */}
            {regenInfo && (
              <div className="mt-6 rounded-2xl bg-[#0a0a0a] border border-white/10 p-4" data-testid="regen-actions">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <ArrowsClockwise size={16} weight="bold" className="text-[#B4F34A]" />
                    <p className="text-sm font-semibold">Cambios extras</p>
                  </div>
                  <Link to="/cambios-extra" className="text-xs text-[#B4F34A] hover:underline" data-testid="regen-go-buy">
                    Saldo: {regenInfo.regen_credits || 0} · Comprar más
                  </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {/* Regenerate Audio */}
                  <button
                    onClick={regenerateAudio}
                    disabled={regenLoading}
                    className="text-left rounded-xl bg-[#121212] border border-white/10 hover:border-[#B4F34A]/40 transition-colors p-3 disabled:opacity-50"
                    data-testid="regen-audio-btn"
                  >
                    <div className="flex items-center gap-2">
                      <ArrowsClockwise size={14} className="text-[#B4F34A]" />
                      <span className="font-display font-semibold text-sm">Regenerar audio</span>
                    </div>
                    <p className="text-[11px] text-white/50 mt-1">
                      {regenInfo.kinds?.audio?.is_free_next
                        ? "1 cambio gratis incluido"
                        : `$20 MXN (1 cambio extra) · usados: ${regenInfo.kinds?.audio?.used || 0}`}
                    </p>
                  </button>

                  {/* Regenerate Cover */}
                  <button
                    onClick={() => {
                      const credits = regenInfo.regen_credits || 0;
                      if (credits <= 0) { nav("/cambios-extra"); return; }
                      coverInputRef.current?.click();
                    }}
                    disabled={regenLoading}
                    className="text-left rounded-xl bg-[#121212] border border-white/10 hover:border-[#B4F34A]/40 transition-colors p-3 disabled:opacity-50"
                    data-testid="regen-cover-btn"
                  >
                    <div className="flex items-center gap-2">
                      <Upload size={14} className="text-[#B4F34A]" />
                      <span className="font-display font-semibold text-sm">Cambiar portada</span>
                    </div>
                    <p className="text-[11px] text-white/50 mt-1">
                      $20 MXN (1 cambio extra) · sin cambio gratis incluido
                    </p>
                  </button>
                </div>

                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onCoverFile}
                  className="hidden"
                  data-testid="regen-cover-input"
                />
              </div>
            )}
          </>
        ) : generatingAudio ? (
          <div className="rounded-2xl bg-[#121212] border border-[#FFB800]/20 p-8 text-center" data-testid="song-generating-panel">
            <div className="soundwave mx-auto mb-5" aria-hidden="true">
              <span></span><span></span><span></span><span></span><span></span><span></span><span></span>
            </div>
            <MusicNotes size={28} weight="fill" className="mx-auto text-[#FFB800] mb-2 animate-pulse-slow" />
            <p className="font-display text-xl font-bold mb-1 pulse-title">Componiendo tu canción…</p>
            <p className="text-white/50 text-sm">
              Nuestra IA musical está grabando tu canción con voz e instrumentos reales.
              Esto toma entre 1 y 2 minutos. No cierres esta página.
            </p>
          </div>
        ) : audioFailed ? (
          <div className="rounded-2xl bg-[#121212] border border-red-500/20 p-6 text-center" data-testid="song-failed-panel">
            <Warning size={32} weight="fill" className="mx-auto text-red-400 mb-3" />
            <p className="font-display text-xl font-bold mb-1">Hubo un problema generando el audio</p>
            <p className="text-white/50 text-sm mb-4">No te preocupes, puedes reintentar sin costo.</p>
            <button
              onClick={retryAudio}
              disabled={regenLoading}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFB800] text-black font-semibold disabled:opacity-50"
              data-testid="song-retry-audio-btn"
            >
              <ArrowsClockwise size={16} weight="bold" /> Reintentar gratis
            </button>
          </div>
        ) : song.payment_status === "paid" ? (
          // Paid (via credit/PIN) but audio not generated yet — the user just needs
          // to finish the creation flow (approve lyrics → cover → audio).
          <div className="rounded-2xl bg-[#121212] border border-[#FFB800]/30 p-6 text-center" data-testid="song-in-progress-panel">
            <MusicNotes size={32} weight="fill" className="mx-auto text-[#FFB800] mb-3" />
            <p className="font-display text-xl font-bold mb-1">Sigue creando tu canción</p>
            <p className="text-white/60 text-sm mb-4">
              Ya tienes la letra. Te falta {song.lyrics_approved ? "elegir la portada y generar el audio" : "aprobar la letra, elegir portada y generar el audio"}.
            </p>
            <Link to={`/create?song_id=${song.id}`} className="inline-block px-6 py-3 rounded-full bg-[#FFB800] text-black font-semibold" data-testid="song-resume-flow">
              Continuar →
            </Link>
          </div>
        ) : (
          <div className="rounded-2xl bg-[#121212] border border-white/10 p-6 text-center">
            <Lock size={32} className="mx-auto text-white/30 mb-3" />
            <p className="font-display text-xl font-bold mb-1">Canción bloqueada</p>
            <p className="text-white/50 text-sm mb-4">Completa el pago de $49 MXN para escuchar y descargar.</p>
            <Link to={`/create?song_id=${song.id}`} className="inline-block px-6 py-3 rounded-full bg-[#FFB800] text-black font-semibold" data-testid="song-go-pay">
              Continuar al pago
            </Link>
          </div>
        )}

        {/* Viral referral: free song for sharing (while waiting or when ready) */}
        {(paid || generatingAudio) && <ReferralCard />}

        {/* Lyrics */}
        <div className="mt-10">
          <h3 className="text-sm uppercase tracking-widest text-white/40 mb-3">Letra</h3>
          <pre className="whitespace-pre-wrap font-sans text-white/80 leading-loose bg-[#0a0a0a] border border-white/5 rounded-2xl p-5" data-testid="song-lyrics">
{song.lyrics}
          </pre>
        </div>
      </div>

      <CommercialAuthDialog
        songId={id}
        songTitle={song.title}
        open={commercialOpen}
        onClose={() => setCommercialOpen(false)}
        onSuccess={load}
      />

      <RegenChoiceDialog
        open={regenDialogOpen}
        onClose={() => setRegenDialogOpen(false)}
        freeLyrics={!!regenInfo?.kinds?.lyrics?.is_free_next}
        freeAudio={!!regenInfo?.kinds?.audio?.is_free_next}
        regenCredits={regenInfo?.regen_credits || 0}
        onChoose={handleRegenChoice}
      />

      {/* Modal de confirmación al pedir nueva versión del audio */}
      <AudioRegenConfirmModal
        open={showAudioRegenConfirm}
        regenInfo={regenInfo}
        loading={regenLoading}
        onCancel={() => setShowAudioRegenConfirm(false)}
        onConfirm={confirmAudioRegen}
        onBuyMore={() => { setShowAudioRegenConfirm(false); nav("/cambios-extra"); }}
      />
    </div>
  );
}
